// Files a digital print quote links to: the configured capture and a
// customer's own image. They go to the S3 upload cache the quote form uses
// (Shrine's presign endpoint), then mkt copies them to public storage so the
// lead's links outlive the cache.
import { logErrorToSentry } from '@Helpers/helpers';

const PRESIGN_PATH = '/s3/params';
const STORE_PATH = '/digital-printing/uploads';

const uploadToCache = async (blob, filename) => {
  const query = new URLSearchParams({
    filename,
    type: blob.type || 'application/octet-stream',
  });
  const presign = await fetch(`${PRESIGN_PATH}?${query}`, {
    headers: { Accept: 'application/json' },
  });
  if (!presign.ok) throw new Error(`S3 presign failed: ${presign.status}`);
  const { url, fields } = await presign.json();

  const body = new FormData();
  Object.entries(fields).forEach(([key, value]) => body.append(key, value));
  body.append('file', blob, filename);
  const upload = await fetch(url, { method: 'POST', body });
  if (!upload.ok) throw new Error(`S3 upload failed: ${upload.status}`);

  // Shrine knows a cached file by its key under the cache prefix.
  return fields.key.replace(/^cache\//, '');
};

// The public URL of a permanent copy, or null if any step fails: the quote
// still goes, without that link.
export const storeDesignFile = async (blob, filename) => {
  try {
    const id = await uploadToCache(blob, filename);
    const response = await fetch(STORE_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id }),
    });
    if (!response.ok) {
      throw new Error(`Storing ${filename} failed: ${response.status}`);
    }
    const { url } = await response.json();
    return url || null;
  } catch (error) {
    logErrorToSentry(error);
    return null;
  }
};

export const dataUrlFrom = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
